import {uploadGeometry} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/geometry-helpers"
import {
    Operator,
    OperatorFlags,
    OperatorPanelComponentType,
    OperatorProcessingHints,
} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {OperatorBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-base"
import {OperatorCallback} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-callback"
import {
    createGridMappingGeometry,
    GridPoint,
    ResultType as GridMappingResultType,
} from "@app/textures/texture-editor/operator-stack/operators/tiling/helpers/create-grid-mapping-geometry"
import {TilingPanelComponent} from "@app/textures/texture-editor/operator-stack/operators/tiling/panel/tiling-panel.component"
import {Vector} from "@app/textures/texture-editor/operator-stack/operators/tiling/toolbox/helper-lines/edit-vector-item"
import {BoundaryCurveControlPoint} from "@app/textures/texture-editor/operator-stack/operators/tiling/toolbox/tiling-area/boundary-curve-control-point"
import {ChangeEvent} from "@app/textures/texture-editor/operator-stack/operators/tiling/toolbox/tiling-area/boundary-curve-item"
import {BoundaryDirection, BoundaryItem} from "@app/textures/texture-editor/operator-stack/operators/tiling/toolbox/tiling-area/boundary-item"
import {ViewMode} from "@app/textures/texture-editor/operator-stack/operators/tiling/toolbox/tiling-area/spatial-mapping-item"
import {TilingToolbox} from "@app/textures/texture-editor/operator-stack/operators/tiling/toolbox/tiling-toolbox"
import {JobNodes} from "@cm/job-nodes"
import {Size2Like, Vector2, Vector2Like} from "@cm/math"
import {deepCopy} from "@cm/utils"
import {AsyncReentrancyGuard} from "@cm/utils/async-reentrancy-guard"
import {Hotkeys} from "@common/services/hotkeys/hotkeys.service"
import {UploadGqlService} from "@common/services/upload/upload.gql.service"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {signal} from "@angular/core"
import {BehaviorSubject, filter, merge, Subject, takeUntil} from "rxjs"
import {TextureType} from "@generated"
import {computeTValuesForBoundary, subdivideTValues} from "@app/textures/texture-editor/operator-stack/operators/tiling/toolbox/tiling-area/utils"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {Geometry} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/rasterize-geometry/definition"
import {
    AnyImageOpResult,
    AnyImageOpResultNode,
    ValueOrNode,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeTilingExtractBorder} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/extract-border/node"
import {ImageOpNodeTilingApplyMapping} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/apply-mapping/node"
import {BorderDirection} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/extract-border/definition"
import {z} from "zod/v4-mini"
import {ImageOpNodeRotateTextureType} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/composite/rotate-texture-type/node"
import {ImageOpNodeGenerateBorderBlendMask} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/generate-border-blend-mask/node"
import {ImageOpNodeTilingBlendBorder} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/blend-border/node"
import {ImageOpNodeTilingAssembleResult} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/assemble-result/node"
import {ImageOpNodeDataObject} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/data-object/node"
import {BakedMaskCache} from "@app/textures/texture-editor/operator-stack/operators/shared/nodes/mask-brush-bake/baked-mask-cache"
import {ImageOpNodeTilingFindBestMatchAlongSegment} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/find-best-match-along-segment/node"
import {HelperLineTracer} from "@app/textures/texture-editor/operator-stack/operators/tiling/helpers/helper-line-tracer"
import {ImageOpNodeTilingHierarchicalCrossCorrelation} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/hierarchical-cross-correlation/node"
import {ImageOpHalOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"

export class OperatorTiling extends OperatorBase<TextureEditNodes.OperatorTiling> {
    // OperatorBase
    override readonly flags = new Set<OperatorFlags>(["no-clone", "no-disable", "apply-to-all-texture-types"])

    readonly panelComponentType: OperatorPanelComponentType = TilingPanelComponent
    readonly canvasToolbox: TilingToolbox

    readonly type = "operator-tiling" as const

    readonly showGuides$: BehaviorSubject<boolean>
    readonly viewMode$: BehaviorSubject<ViewMode>
    readonly snapToHelperLinesEnabled$: BehaviorSubject<boolean>
    readonly snapToSimilarFeatureEnabled$: BehaviorSubject<boolean>
    readonly snapDistancePx$: BehaviorSubject<number>
    readonly boundaryFollowsHelperLinesEnabled$: BehaviorSubject<boolean>
    readonly alignmentSpacingPx$: BehaviorSubject<number>
    readonly alignmentSearchSizeRatio$: BehaviorSubject<number>
    readonly alignmentMinCorrelation$: BehaviorSubject<number>
    readonly alignmentCorrelationPenaltyAlongEdge$: BehaviorSubject<number>
    readonly alignmentCorrelationPenaltyAcrossEdge$: BehaviorSubject<number>
    readonly borderBlendEnabled$: BehaviorSubject<boolean>
    readonly borderBlendDistancePx$: BehaviorSubject<number>
    readonly cutoutAreaOpacity$ = new BehaviorSubject<number>(0.1)
    readonly debugDrawEnabled$ = new BehaviorSubject(false)
    readonly testParam1$ = new BehaviorSubject<number>(0)
    readonly testParam2$ = new BehaviorSubject<number>(0)

    constructor(callback: OperatorCallback, node: TextureEditNodes.OperatorTiling | null) {
        const sourceSize = {
            width: callback.textureTypeData?.sourceDataObject?.width ?? 0,
            height: callback.textureTypeData?.sourceDataObject?.height ?? 0,
        }
        const sourceMinSize = Math.min(sourceSize.width, sourceSize.height)
        const initialControlPointInset = sourceMinSize * initialControlPointInsetRatio
        super(
            callback,
            deepCopy(node) ?? {
                type: "operator-tiling",
                enabled: true,
                cornerControlPoints: {
                    topLeft: {
                        positionPx: {
                            x: initialControlPointInset,
                            y: initialControlPointInset,
                        },
                    },
                    topRight: {
                        positionPx: {
                            x: sourceSize.width - initialControlPointInset,
                            y: initialControlPointInset,
                        },
                    },
                    bottomLeft: {
                        positionPx: {
                            x: initialControlPointInset,
                            y: sourceSize.height - initialControlPointInset,
                        },
                    },
                    bottomRight: {
                        positionPx: {
                            x: sourceSize.width - initialControlPointInset,
                            y: sourceSize.height - initialControlPointInset,
                        },
                    },
                },
                boundaries: {
                    horizontal: {
                        controlPoints: [],
                    },
                    vertical: {
                        controlPoints: [],
                    },
                },
                display: {
                    showGuides: true,
                    viewMode: "source",
                },
                helperLines: {
                    curves: [],
                    boundaryFollows: true,
                },
                alignment: {
                    controlPointSpacingPx: 128,
                    searchSizeRatio: 1,
                    minCorrelation: 0.3,
                    correlationPenaltyAlongEdge: 0.3,
                    correlationPenaltyAcrossEdge: 0.3,
                },
                borderBlending: {
                    enabled: false,
                    widthPx: 128,
                },
            },
        )

        this.uploadService = this.callback.injector.get(UploadGqlService)

        // bidirectional binding from node to UI
        const biBind = <T>(initialValue: T, setter: (value: T) => void, markEdited = true) => {
            const obs = new BehaviorSubject(initialValue)
            let isInitialValue = true
            obs.pipe(takeUntil(this.destroyed)).subscribe((value) => {
                setter(value)
                if (!isInitialValue && markEdited) {
                    this.markEdited()
                }
                isInitialValue = false
            })
            return obs
        }
        this.showGuides$ = biBind(this.node.display.showGuides, (value) => (this.node.display.showGuides = value), false)
        this.viewMode$ = biBind(this.node.display.viewMode as ViewMode, (value) => (this.node.display.viewMode = value), false)
        this.alignmentSpacingPx$ = biBind(this.node.alignment.controlPointSpacingPx, (value) => {
            this.node.alignment.controlPointSpacingPx = value
            this.removeAlignmentInfo()
        })
        this.alignmentSearchSizeRatio$ = biBind(this.node.alignment.searchSizeRatio, (value) => {
            this.node.alignment.searchSizeRatio = value
            this.removeAlignmentInfo()
        })
        this.alignmentCorrelationPenaltyAlongEdge$ = biBind(this.node.alignment.correlationPenaltyAlongEdge ?? 0, (value) => {
            this.node.alignment.correlationPenaltyAlongEdge = value
            this.removeAlignmentInfo()
        })
        this.alignmentCorrelationPenaltyAcrossEdge$ = biBind(this.node.alignment.correlationPenaltyAcrossEdge ?? 0, (value) => {
            this.node.alignment.correlationPenaltyAcrossEdge = value
            this.removeAlignmentInfo()
        })
        this.alignmentMinCorrelation$ = biBind(this.node.alignment.minCorrelation, (value) => {
            this.node.alignment.minCorrelation = value
            this.updateAlignmentControlPoints()
        })
        this.borderBlendEnabled$ = biBind(this.node.borderBlending.enabled, (value) => (this.node.borderBlending.enabled = value))
        this.borderBlendDistancePx$ = biBind(this.node.borderBlending.widthPx, (value) => (this.node.borderBlending.widthPx = value))
        this.boundaryFollowsHelperLinesEnabled$ = biBind(this.node.helperLines?.boundaryFollows ?? false, (value) => {
            if (!this.node.helperLines) {
                this.node.helperLines = {
                    curves: [],
                    boundaryFollows: value,
                }
            } else {
                this.node.helperLines.boundaryFollows = value
            }
            this.removeAlignmentInfo()
        })

        this.canvasToolbox = new TilingToolbox(this)

        this.snapToHelperLinesEnabled$ = new BehaviorSubject(true)
        this.snapToSimilarFeatureEnabled$ = new BehaviorSubject(false)
        this.snapDistancePx$ = new BehaviorSubject(1024)

        this.canvasToolbox.helperLinesBag.traceVectorCreated.subscribe((vector) => this.onTraceVectorCreated(vector))
        merge(this.canvasToolbox.helperLinesBag.helperLineCreated, this.canvasToolbox.helperLinesBag.helperLineRemoved).subscribe(() => this.markEdited())
        this.canvasToolbox.helperLinesBag.helperLineCreated.subscribe((helperLine) => {
            helperLine.selectedChange.subscribe(() => this.updateHasSelectedHelperLines())
            this.updateHasSelectedHelperLines()
        })
        this.canvasToolbox.helperLinesBag.helperLineRemoved.subscribe(() => this.updateHasSelectedHelperLines())

        // restore helper lines
        if (this.node.helperLines) {
            for (const curve of this.node.helperLines.curves) {
                const points = curve.points.map((point) => Vector2.fromVector2Like(point))
                const helperLine = this.canvasToolbox.helperLinesBag.createHelperLine(curve.origin ?? points[0], curve.target ?? points[1])
                helperLine.setPoints(points)
                helperLine.state = "finished"
            }
        }

        this.callback.injector.get(Hotkeys).addShortcuts({
            bindings: [
                {keys: ["k"], action: () => this.viewMode$.next(ViewMode.Source)},
                {keys: ["l"], action: () => this.viewMode$.next(ViewMode.Result)},
                {keys: ["s"], action: () => this.toggleSnapMode()},
                {keys: ["v"], action: () => this.showGuides$.next(!this.showGuides$.value)},
            ],
            takeUntilDestroy: takeUntil(this.destroyed),
            guard: () => this.$selected(),
        })

        this.canvasToolbox.tilingArea.areaChanged.subscribe((event) => this.onTilingAreaChanged(event))

        // make sure we remove the alignment data when the user changes the control points or the boundary has changed
        const pointVizBagItem = this.canvasToolbox.tilingArea.spatialMapping.pointVizBagItem
        merge(pointVizBagItem.pointCreated, pointVizBagItem.pointRemoved, pointVizBagItem.pointMoved)
            .pipe(
                filter((pointVizItem) => pointVizItem.type === "user"),
                takeUntil(this.unsubscribe),
            )
            .subscribe(() => this.removeAlignmentInfo())

        merge(this.viewMode$, this.debugDrawEnabled$, this.testParam1$, this.testParam2$).subscribe(() => this.requestEval())
        // dragEnd is needed here because we don't want to border blend during dragging which needs recalc of the grid
        merge(this.borderBlendDistancePx$, pointVizBagItem.dragEnd).subscribe(() => {
            this.invalidateBorderBlendMask() // we need to new mask if the border changes
            this.invalidateCachedGrid() // we need to new grid if the border changes
            this.requestEval()
        })
        this.borderBlendEnabled$.subscribe(() => {
            this.invalidateCachedGrid() // we need to new grid if the border changes
            this.requestEval()
        })

        const getUploadedGeometryDescriptor = (uploadedGridMapping: TextureEditNodes.OperatorTiling["uploadedGridMapping"]) => {
            return uploadedGridMapping
                ? {
                      dataObjectId: Promise.resolve(uploadedGridMapping.meshDataObject.dataObjectId),
                      resultSize: uploadedGridMapping.resultSize,
                  }
                : undefined
        }
        this.uploadedGridGeometry = getUploadedGeometryDescriptor(this.node.uploadedGridMapping)
        this.canvasToolbox.update()

        this.isInitializing = false

        this.resetEdited()
    }

    // OperatorBase
    // TODO
    // override async init() {
    //     await super.init()
    //
    //     const createBorderBlendMaskHandle = async (maskReference: TextureEditNodes.MaskReference | undefined) => {
    //         if (!maskReference) {
    //             return undefined
    //         }
    //         return this.callback.imageOpContextWebGL2.createDrawableImageHandle({
    //             dataObjectId: maskReference.dataObjectId,
    //             channelLayout: "R",
    //             dataType: "uint8",
    //         })
    //     }
    //     const [horizontalBorderBlendMask, verticalBorderBlendMask] = await Promise.all([
    //         createBorderBlendMaskHandle(this.node.borderBlending.horizontalMask),
    //         createBorderBlendMaskHandle(this.node.borderBlending.verticalMask),
    //     ])
    //     this.horizontalBorderBlendMask = horizontalBorderBlendMask
    //     this.verticalBorderBlendMask = verticalBorderBlendMask
    // }

    // OperatorBase
    override dispose(): void {
        this.abortAllTraces()
        this.snapToSimilarFeatureImageOpNodeBag.dispose()
        this.sharedImageOpNodeBag.dispose()
        this.destroyed.next()
        this.destroyed.complete()
        super.dispose()
        this.invalidateBorderBlendMask()
        this.canvasToolbox.remove()
    }

    // OperatorBase
    async clone(): Promise<Operator> {
        throw Error("Tiling operator can not be cloned")
    }

    override createImageOpGraph(
        sourceImage: AnyImageOpResultNode,
        hints: OperatorProcessingHints,
        imageOpNodeBag: ImageOpNodeBag,
    ): Promise<AnyImageOpResultNode> {
        return this.sharedImageOpNodeBag.runInFrame(() => this.createImageOpGraphInSharedFrame(sourceImage, hints, imageOpNodeBag))
    }

    private async createImageOpGraphInSharedFrame(
        sourceImage: AnyImageOpResultNode,
        hints: OperatorProcessingHints,
        imageOpNodeBag: ImageOpNodeBag,
    ): Promise<AnyImageOpResultNode> {
        // Wrap sourceImage in a lambda to capture its dimensions as a side effect during evaluation.
        // This gives us the correct source size on subsequent evaluations (falls back to sourceDataObject on the first).
        const sourceImageWithSizeCapture = imageOpNodeBag.getLambda("captureSourceSize", {sourceImage}, this._captureSourceSize)

        const spatialMapping = this.canvasToolbox.tilingArea.spatialMapping
        this._maxBorderBlendingPx = Math.floor(Math.min(spatialMapping.boundaryH.mappedLength, spatialMapping.boundaryV.mappedLength) / 2)
        const borderBlendEnabled = this.borderBlendEnabled$.value && !spatialMapping.pointVizBagItem.isDraggingControlPoint
        const borderBlendDistance = borderBlendEnabled
            ? Math.round(Math.max(this.minBorderBlendingPx, Math.min(this.maxBorderBlendingPx, this.borderBlendDistancePx$.value)))
            : 0
        if (!this.cachedGridGeometry) {
            let tValuesH = computeTValuesForBoundary(spatialMapping.boundaryH)
            let tValuesV = computeTValuesForBoundary(spatialMapping.boundaryV)
            if (borderBlendDistance > 0) {
                const tBorderH = borderBlendDistance / spatialMapping.mappedSize.x
                const tBorderV = borderBlendDistance / spatialMapping.mappedSize.y
                tValuesH = [-tBorderH, ...tValuesH, 1 + tBorderH]
                tValuesV = [-tBorderV, ...tValuesV, 1 + tBorderV]
            }
            while (tValuesH.length < 16) {
                tValuesH = subdivideTValues(tValuesH, this.numGridControlPointSegmentSubdivisions)
            }
            while (tValuesV.length < 16) {
                tValuesV = subdivideTValues(tValuesV, this.numGridControlPointSegmentSubdivisions)
            }
            const tessellatedGridPoints = spatialMapping.computeGridPoints(tValuesH, tValuesV)
            this._pendingValidationData = {tessellatedGridPoints, borderBlendDistance}
            this.cachedGridGeometry = createGridMappingGeometry({
                gridPoints: tessellatedGridPoints,
                targetPixelOffset: {
                    x: borderBlendDistance,
                    y: borderBlendDistance,
                },
            })
        }
        const cachedGridGeometry = this.cachedGridGeometry

        // resolve gridGeometry/resultSize and build mappedImage once, regardless of whether the graph below
        // is actually reached this pass (source view) -- keeps the "applyMapping" node's identity/shape
        // stable across frames whether or not the short circuit below ends up returning early
        let gridGeometry: Geometry | JobNodes.DataObjectReference
        let resultSize: Size2Like
        if (hints.mode !== "final") {
            gridGeometry = cachedGridGeometry.geometry
            resultSize = cachedGridGeometry.resultSize
        } else {
            // final
            if (!this.uploadedGridGeometry) {
                // upload the geometry so we can reuse it for the different texture types (rather than having them embedded in every image-processing graph)
                const organizationId = this.callback.organization.id
                const promisedDataObjectId = uploadGeometry(this.uploadService, organizationId, cachedGridGeometry.geometry, "grid-geometry.json").then(
                    (result) => result.id,
                )
                this.uploadedGridGeometry = {
                    dataObjectId: promisedDataObjectId,
                    resultSize: cachedGridGeometry.resultSize,
                }
            }
            gridGeometry = await this.uploadedGridGeometry.dataObjectId.then((dataObjectId) => JobNodes.dataObjectReference(dataObjectId))
            resultSize = this.uploadedGridGeometry.resultSize
        }

        const mappedImage = imageOpNodeBag.getNode("applyMapping", ImageOpNodeTilingApplyMapping, {
            sourceImage: sourceImageWithSizeCapture,
            gridGeometry,
            resultSize,
        })

        const computeBorder = async (
            borderDirection: z.infer<typeof BorderDirection>,
            mappedImage: ValueOrNode<AnyImageOpResult>,
            maskBakeCache: BakedMaskCache,
            maskDataObjectReference: TextureEditNodes.DataObjectReference,
        ) => {
            const extractedBorder = imageOpNodeBag.getNode(borderDirection + "ExtractBorder", ImageOpNodeTilingExtractBorder, {
                mappedImage,
                borderBlendDistance,
                borderDirection,
            })
            const lowBorder = imageOpNodeBag.getProperty(borderDirection + "LowBorder", extractedBorder, "lowBorder")
            const highBorder = imageOpNodeBag.getProperty(borderDirection + "HighBorder", extractedBorder, "highBorder")
            let maskImage: ValueOrNode<AnyImageOpResult>
            if (maskDataObjectReference.dataObjectId) {
                // nothing changed since the last bake -- no HAL/upload work needed at all
                maskImage = imageOpNodeBag.getNode(borderDirection + "LoadMask", ImageOpNodeDataObject, {
                    dataObjectId: maskDataObjectReference.dataObjectId,
                    downloadFormat: "exr",
                    channels: 1,
                })
            } else {
                const liveMask: ValueOrNode<AnyImageOpResult> = this.sharedImageOpNodeBag.getNode(
                    borderDirection + "GenerateMask",
                    ImageOpNodeGenerateBorderBlendMask,
                    {lowBorder, highBorder, borderDirection},
                )
                if (hints.mode === "final") {
                    // uploads the value captured during a prior "preview"/"pre-final" pass through the else
                    // branch below -- this is the one place an actual upload happens
                    const {dataObjectId} = await maskBakeCache.getDataObjectId()
                    maskDataObjectReference.dataObjectId = dataObjectId
                    maskImage = imageOpNodeBag.getNode(borderDirection + "LoadBakedMask", ImageOpNodeDataObject, {
                        dataObjectId,
                        downloadFormat: "exr",
                        channels: 1,
                    })
                } else {
                    // preview: reuse an already-captured mask when one exists, otherwise generate live and capture it
                    maskImage = maskBakeCache.nodeFromPendingOrCaptureViaGraph(imageOpNodeBag, borderDirection + "CaptureMask", liveMask)
                }
            }
            const blendedBorder = imageOpNodeBag.getNode(borderDirection + "BlendBorder", ImageOpNodeTilingBlendBorder, {
                lowBorder,
                highBorder,
                mask: maskImage,
                useLaplacianBlending: true,
                debugDrawEnabled: this.debugDrawEnabled$.value,
                testParam1: this.testParam1$.value,
                testParam2: this.testParam2$.value,
            })
            return imageOpNodeBag.getNode(borderDirection + "Assemble", ImageOpNodeTilingAssembleResult, {
                mappedImage,
                blendedBorder,
                borderDirection,
            })
        }
        if (borderBlendDistance > 0) {
            if (!this.node.borderBlending.horizontalMask) {
                this.node.borderBlending.horizontalMask = {
                    type: "data-object-reference",
                    dataObjectId: "",
                }
            }
            if (!this.node.borderBlending.verticalMask) {
                this.node.borderBlending.verticalMask = {
                    type: "data-object-reference",
                    dataObjectId: "",
                }
            }
        }

        if (hints.mode === "preview" && this.selected && this.viewMode$.value === ViewMode.Source) {
            return sourceImageWithSizeCapture
        }

        let resultImage: AnyImageOpResultNode = mappedImage
        if (borderBlendDistance > 0 && this.node.borderBlending.horizontalMask && this.node.borderBlending.verticalMask) {
            resultImage = await computeBorder("horizontal", resultImage, this.horizontalBorderMaskCache, this.node.borderBlending.horizontalMask)
            resultImage = await computeBorder("vertical", resultImage, this.verticalBorderMaskCache, this.node.borderBlending.verticalMask)
        }
        resultImage = imageOpNodeBag.getNode("rotateTextureType", ImageOpNodeRotateTextureType, {
            sourceImage: resultImage,
            angleInDegrees: this.computeAverageMappingAngleInDegrees(),
            textureType: hints.textureType,
        })

        return resultImage
    }

    private invalidateBorderBlendMask() {
        if (this.isInitializing) {
            return
        }
        if (this.node.borderBlending.horizontalMask) {
            this.node.borderBlending.horizontalMask.dataObjectId = ""
        }
        if (this.node.borderBlending.verticalMask) {
            this.node.borderBlending.verticalMask.dataObjectId = ""
        }
        this.horizontalBorderMaskCache.dispose()
        this.verticalBorderMaskCache.dispose()
    }

    get isCreatingHelperLineTrace() {
        return this.$traceHelperLineEditModeEnabled()
    }

    setTraceHelperLineEditModeEnabled(enabled: boolean) {
        this.$traceHelperLineEditModeEnabled.set(enabled)
        if (enabled) {
            this.showGuides$.next(true) // make sure guides are shown when tracing
        }
        this.canvasToolbox.helperLinesBag.setHelperLineTracingMode(enabled)
    }

    private onTraceVectorCreated(vector: Vector) {
        this.setTraceHelperLineEditModeEnabled(false)
        this.traceHelperLine(vector)
    }

    private traceHelperLine(vector: Vector) {
        const helperLine = this.canvasToolbox.helperLinesBag.createHelperLine(vector.from, vector.to)
        helperLine.selected = true
        const tracer = new HelperLineTracer(this.callback, helperLine)
        this.$currentHelperLineTracer.set(tracer)
        tracer.traceComplete.subscribe(() => {
            if (helperLine === this.$currentHelperLineTracer()?.helperLine) {
                this.$currentHelperLineTracer.set(undefined)
            }
            if (helperLine.state !== "removed") {
                helperLine.state = "finished"
            }
        })
        return helperLine
    }

    private abortAllTraces() {
        this.$currentHelperLineTracer()?.abort()
    }

    get isTracingHelperLines() {
        return !!this.$currentHelperLineTracer()
    }

    private computeAverageMappingAngleInDegrees() {
        const tilingArea = this.canvasToolbox.tilingArea
        const lowHorizontalCurveControlPoints = tilingArea.spatialMapping.boundaryH.curveMin.curveControlPoints
        const highHorizontalCurveControlPoints = tilingArea.spatialMapping.boundaryH.curveMax.curveControlPoints
        const p00 = lowHorizontalCurveControlPoints[0].controlPoint.sourcePosition
        const p10 = lowHorizontalCurveControlPoints[lowHorizontalCurveControlPoints.length - 1].controlPoint.sourcePosition
        const p01 = highHorizontalCurveControlPoints[0].controlPoint.sourcePosition
        const p11 = highHorizontalCurveControlPoints[highHorizontalCurveControlPoints.length - 1].controlPoint.sourcePosition
        const h0 = Vector2.fromVector2Like(p10).subInPlace(p00)
        const h1 = Vector2.fromVector2Like(p11).subInPlace(p01)
        const v0 = Vector2.fromVector2Like(p01).subInPlace(p00)
        const v1 = Vector2.fromVector2Like(p11).subInPlace(p10)
        // average vectors in order to get the average angle
        const avgH = h0.add(h1)
        const avgV = v0.add(v1)
        const avgVector = avgH.sub(avgV.perp())
        const avgAngle = -Math.atan2(avgVector.y, avgVector.x)
        return avgAngle * (180 / Math.PI)
    }

    async createAlignmentInfo() {
        // in case the texture type has changed, we need to remove the alignment info and recompute it
        const textureType = this.callback.selectedTextureType
        if (this.$alignmentInfo() && this.$alignmentInfo()!.textureType !== textureType) {
            this.removeAlignmentInfo()
        }
        if (!this.isAlignmentDataAvailable) {
            this.$isComputingAlignment.set(true)
            try {
                const result = await this.alignBoundaries()
                this.$alignmentInfo.set({
                    textureType,
                    points: result?.alignmentPointInfos ?? [],
                    alignmentQuality: Math.max(0, result?.minCorrelation ?? 0),
                })
            } finally {
                this.$isComputingAlignment.set(false)
            }
        }
        this.updateAlignmentControlPoints()
    }

    removeAlignmentInfo() {
        this.removeAlignmentControlPoints()
        this.$alignmentInfo.set(undefined)
    }

    updateAlignmentControlPoints() {
        if (!this.$alignmentInfo()) {
            return
        }
        const tilingArea = this.canvasToolbox.tilingArea
        for (const pointInfo of this.$alignmentInfo()!.points) {
            const boundary = tilingArea.spatialMapping.getBoundary(pointInfo.boundaryDirection)
            const isAccepted = pointInfo.snapResult.correlation >= this.alignmentMinCorrelation$.value
            if (isAccepted) {
                if (!pointInfo.curveControlPoints) {
                    pointInfo.curveControlPoints = boundary.insertControlPoints(
                        "alignment",
                        pointInfo.t,
                        pointInfo.referencePosition,
                        pointInfo.snapResult.snappedPosition,
                    )
                } else {
                    pointInfo.curveControlPoints.curveControlPoint1.controlPoint.sourcePosition = pointInfo.snapResult.snappedPosition
                }
            } else {
                if (pointInfo.curveControlPoints) {
                    tilingArea.spatialMapping.pointVizBagItem.deleteControlPoints([
                        pointInfo.curveControlPoints.curveControlPoint0.controlPoint,
                        pointInfo.curveControlPoints.curveControlPoint1.controlPoint,
                    ])
                    pointInfo.curveControlPoints = undefined
                }
            }
        }
        this.$hasAlignmentControlPoints.set(this.canvasToolbox.tilingArea.spatialMapping.pointVizBagItem.hasControlPointsOfType("alignment"))
    }

    removeAlignmentControlPoints() {
        this.canvasToolbox?.tilingArea.spatialMapping.pointVizBagItem.deleteControlPointsOfType("alignment")
        this.$alignmentInfo()?.points.forEach((pointInfo) => (pointInfo.curveControlPoints = undefined))
        this.$hasAlignmentControlPoints.set(false)
    }

    get isAlignmentDataAvailable() {
        return this.$alignmentInfo() !== undefined
    }

    get isComputingAlignment() {
        return this.$isComputingAlignment()
    }

    get hasAlignmentControlPoints() {
        return this.$hasAlignmentControlPoints()
    }

    get hasLowQualityAlignment() {
        if (!this.$alignmentInfo()) {
            return false
        }
        return this.$alignmentInfo()!.alignmentQuality < 0.1
    }

    get hasHelperLines() {
        return this.canvasToolbox.helperLinesBag.helperLines.length > 0
    }

    get selectedHelperLines() {
        return this.canvasToolbox.helperLinesBag.helperLines.filter((helperLine) => helperLine.selected)
    }

    get hasSelectedHelperLines() {
        return this.$hasSelectedHelperLines()
    }

    private updateHasSelectedHelperLines() {
        this.$hasSelectedHelperLines.set(this.canvasToolbox.helperLinesBag.helperLines.some((helperLine) => helperLine.selected))
    }

    async retraceSelectedHelperLines() {
        const selectedHelperLines = this.selectedHelperLines
        for (const helperLine of selectedHelperLines) {
            this.canvasToolbox.helperLinesBag.removeHelperLine(helperLine)
            const newHelperLine = this.traceHelperLine({from: helperLine.sourceCastRayOrigin, to: helperLine.sourceCastRayTarget})
            newHelperLine.selected = true
        }
    }

    // convertAlignmentToUserPoints() {
    //     const getAlignmentPointInfos = (boundary: BoundaryItem) => {
    //         const alignmentIndices = boundary.curveMin.curveControlPoints
    //             .map((curveControlPoint, index) => [curveControlPoint, index] as const)
    //             .filter((curveControlPointAndIndex) => curveControlPointAndIndex[0].controlPoint.type === "alignment")
    //             .map((curveControlPointAndIndex) => curveControlPointAndIndex[1])
    //         return alignmentIndices.map((index) => {
    //             const minCurveControlPoint = boundary.curveMin.curveControlPoints[index]
    //             const maxCurveControlPoint = boundary.curveMax.curveControlPoints[index]
    //             return [minCurveControlPoint, maxCurveControlPoint] as const
    //         })
    //     }
    //     const insertAlignmentPointsAsUserPoints = (
    //         boundary: BoundaryItem,
    //         minAndMaxPoint: (readonly [BoundaryCurveControlPoint, BoundaryCurveControlPoint])[],
    //     ) => {
    //         minAndMaxPoint.forEach(([minCurveControlPoint, maxCurveControlPoint]) => {
    //             boundary.insertControlPoints(
    //                 "user",
    //                 minCurveControlPoint.t,
    //                 minCurveControlPoint.controlPoint.sourcePosition,
    //                 maxCurveControlPoint.controlPoint.sourcePosition,
    //             )
    //         })
    //     }
    //     const tilingArea = this.canvasToolbox.tilingArea
    //     const alignmentPointInfosH = getAlignmentPointInfos(tilingArea.spatialMapping.boundaryH)
    //     const alignmentPointInfosV = getAlignmentPointInfos(tilingArea.spatialMapping.boundaryV)
    //     this.removeAlignmentControlPoints()
    //     this.removeAlignmentInfo()
    //     insertAlignmentPointsAsUserPoints(tilingArea.spatialMapping.boundaryH, alignmentPointInfosH)
    //     insertAlignmentPointsAsUserPoints(tilingArea.spatialMapping.boundaryV, alignmentPointInfosV)
    // }

    // updateBoundaryByHelperLines() {
    //     const helperLinesBag = this.canvasToolbox.helperLinesBag
    //     const boundaryH = this.canvasToolbox.tilingArea.spatialMapping.boundaryH
    //     const boundaryV = this.canvasToolbox.tilingArea.spatialMapping.boundaryV
    //     const updateCurve = (curve: BoundaryCurveItem) => {
    //         // find the closest helper line to the control points
    //         const bestHelperLineAndDistance = helperLinesBag.helperLines.reduce(
    //             (bestHelperLineAndSum, helperLine) => {
    //                 const sumDistance = curve.curveControlPoints.reduce((sum, curveControlPoint) => {
    //                     const result = helperLine.getClosestPointAndOffsetToLocation(curveControlPoint.controlPoint.sourcePosition)
    //                     const distance = Vector2.distance(result.closestPoint, curveControlPoint.controlPoint.sourcePosition)
    //                     return sum + distance
    //                 }, 0)
    //                 if (!bestHelperLineAndSum || sumDistance < bestHelperLineAndSum[1]) {
    //                     return [helperLine, sumDistance] as const
    //                 } else {
    //                     return bestHelperLineAndSum
    //                 }
    //             },
    //             undefined as readonly [HelperLineItem, number] | undefined,
    //         )
    //         // align segments
    //         if (bestHelperLineAndDistance) {
    //             const bestHelperLine = bestHelperLineAndDistance[0]
    //             const helperLineInfoPerControlPoint = curve.curveControlPoints.map((curveControlPoint) => {
    //                 const closestPointAndOffset = bestHelperLine.getClosestPointAndOffsetToLocation(curveControlPoint.controlPoint.sourcePosition)
    //                 const tangent = bestHelperLine.getTangentAtOffset(closestPointAndOffset.offset).normalized()
    //                 const normal = tangent.perp()
    //                 return {...closestPointAndOffset, tangent, normal}
    //             })
    //             const curveGuidePoints: [BoundaryControlPoint, number][] = []
    //             for (let i = 1; i < curve.curveControlPoints.length; i++) {
    //                 const controlPoint0 = curve.curveControlPoints[i - 1]
    //                 const controlPoint1 = curve.curveControlPoints[i]
    //                 const helperLineInfo0 = helperLineInfoPerControlPoint[i - 1]
    //                 const helperLineInfo1 = helperLineInfoPerControlPoint[i]
    //                 const controlSegmentDistance0 = Vector2.dot(
    //                     helperLineInfo0.normal,
    //                     controlPoint0.controlPoint.sourcePosition.sub(helperLineInfo0.closestPoint),
    //                 )
    //                 const controlSegmentDistance1 = Vector2.dot(
    //                     helperLineInfo1.normal,
    //                     controlPoint1.controlPoint.sourcePosition.sub(helperLineInfo1.closestPoint),
    //                 )
    //                 const helperLinePoints = bestHelperLine.getPointsForInterval(helperLineInfo0.offset, helperLineInfo1.offset)
    //                 if (helperLinePoints.length < 2) {
    //                     throw new Error("Helper line has too few points")
    //                 }
    //                 let helperLineSegmentLength = 0
    //                 for (let j = 1; j < helperLinePoints.length; j++) {
    //                     helperLineSegmentLength += Vector2.distance(helperLinePoints[j - 1], helperLinePoints[j])
    //                 }
    //                 let currentHelperLineLength = 0
    //                 for (let j = 1; j < helperLinePoints.length - 1; j++) {
    //                     currentHelperLineLength += Vector2.distance(helperLinePoints[j - 1], helperLinePoints[j])
    //                     const t = currentHelperLineLength / helperLineSegmentLength
    //                     const helperLinePoint = helperLinePoints[j]
    //                     const helperLineTangent = bestHelperLine
    //                         .getTangentAtOffset(helperLineInfo0.offset + (helperLineInfo1.offset - helperLineInfo0.offset) * t)
    //                         .normalized()
    //                     const helperLineNormal = helperLineTangent.perp()
    //                     const distance = controlSegmentDistance0 + (controlSegmentDistance1 - controlSegmentDistance0) * t
    //                     const targetPosition = helperLinePoint.add(helperLineNormal.mul(distance))
    //                     const curveT = controlPoint0.t * (1 - t) + controlPoint1.t * t
    //                     const pointVizItem = this.canvasToolbox.tilingArea.spatialMapping.pointVizBagItem.createControlPoint({
    //                         type: "guide",
    //                         sourcePosition: targetPosition,
    //                         resultUV: {
    //                             x: curve.boundary.direction === BoundaryDirection.Horizontal ? curveT : 0,
    //                             y: curve.boundary.direction === BoundaryDirection.Vertical ? curveT : 0,
    //                         },
    //                         deletable: true,
    //                     })
    //                     curveGuidePoints.push([pointVizItem, curveT])
    //                 }
    //             }
    //             curveGuidePoints.forEach((pointVizItem) => curve.createControlPoint(pointVizItem[0], pointVizItem[1]))
    //         }
    //     }
    //     const updateBoundary = (boundary: BoundaryItem) => {
    //         updateCurve(boundary.curveMin)
    //         updateCurve(boundary.curveMax)
    //     }
    //     updateBoundary(boundaryH)
    //     // updateBoundary(boundaryV)
    // }

    private async alignBoundaries() {
        const boundaryH = this.canvasToolbox.tilingArea.spatialMapping.boundaryH
        const boundaryV = this.canvasToolbox.tilingArea.spatialMapping.boundaryV
        this.removeAlignmentControlPoints()
        const searchDistanceAcrossCurve = 32
        const resultH = await this.alignBoundary(boundaryH, {searchDistanceAcrossCurve})
        const resultV = await this.alignBoundary(boundaryV, {searchDistanceAcrossCurve})
        if (resultH && resultV) {
            const minCorrelation = Math.min(resultH.minCorrelation, resultV.minCorrelation)
            const maxCorrelation = Math.max(resultH.maxCorrelation, resultV.maxCorrelation)
            const alignmentPointInfos = resultH.alignmentPointInfos.concat(resultV.alignmentPointInfos)
            return {minCorrelation, maxCorrelation, alignmentPointInfos}
        } else if (resultH) {
            const minCorrelation = resultH.minCorrelation
            const maxCorrelation = resultH.maxCorrelation
            const alignmentPointInfos = resultH.alignmentPointInfos
            return {minCorrelation, maxCorrelation, alignmentPointInfos}
        } else if (resultV) {
            const minCorrelation = resultV.minCorrelation
            const maxCorrelation = resultV.maxCorrelation
            const alignmentPointInfos = resultV.alignmentPointInfos
            return {minCorrelation, maxCorrelation, alignmentPointInfos}
        } else {
            return undefined
        }
    }

    private async alignBoundary(
        boundary: BoundaryItem,
        options: {
            searchDistanceAlongCurve?: number
            searchDistanceAcrossCurve?: number
            distancePenaltyAlongCurve?: number
            distancePenaltyAcrossCurve?: number
        },
    ) {
        this.callback.setBusy(true)

        const alignBoundaryImageOpNodeBag = new ImageOpNodeBag("AlignBoundaryImageOpNodeBag")
        try {
            const imageOpHalOptions: ImageOpHalOptions = {
                usePhysicalOutputImage: true,
            }

            const convertPenalty = (spacing: number, penalty: number) => (spacing > 0 ? (4 * penalty) / spacing : 0)
            const pointSpacing = this.alignmentSpacingPx$.value
            const searchDistanceAlongCurve = options.searchDistanceAlongCurve ?? pointSpacing * 0.5
            const searchDistanceAcrossCurve = Math.round(options.searchDistanceAcrossCurve ?? pointSpacing * 0.5)
            const distancePenaltyAlongCurve = convertPenalty(
                searchDistanceAlongCurve,
                options.distancePenaltyAlongCurve ?? this.alignmentCorrelationPenaltyAlongEdge$.value,
            )
            const distancePenaltyAcrossCurve = convertPenalty(
                searchDistanceAcrossCurve,
                options.distancePenaltyAcrossCurve ?? this.alignmentCorrelationPenaltyAcrossEdge$.value,
            )
            const length = boundary.mappedLength
            const numSteps = Math.ceil(length / pointSpacing)
            const minCurveInterpolator = boundary.curveMin.interpolator
            const maxCurveInterpolator = boundary.curveMax.interpolator

            const referenceSegmentLength = Math.round(pointSpacing)
            const referenceSegmentWidth = 64

            const sourceImage = this.callback.selectedOperatorInput
            if (!sourceImage) {
                throw new Error("No source image")
            }
            let strategy: "forward-integration" | /*"backward-integration" |*/ "fixed" = "forward-integration"
            for (;;) {
                let minCorrelation = 1
                let maxCorrelation = -1
                const alignmentPointInfos: AlignmentPointInfo[] = []
                const stepSize = 1 / (numSteps - 1)
                let currCurveMaxT = 0 // strategy === "backward-integration" ? 1 : 0
                for (let i = 0; i < numSteps; i++) {
                    const t = i / (numSteps - 1)
                    // if (strategy === "backward-integration") {
                    //     t = 1 - t
                    // }
                    const curveMinPos = minCurveInterpolator.evaluate(t)
                    const curveMinTangent = minCurveInterpolator.evaluateTangent(t)
                    if (strategy === "fixed") {
                        currCurveMaxT = t
                    }
                    const curveMaxPos = maxCurveInterpolator.evaluate(currCurveMaxT)
                    const curveMaxTangent = maxCurveInterpolator.evaluateTangent(currCurveMaxT)
                    const referenceSegment = {
                        from: curveMinPos.add(curveMinTangent.mul(-referenceSegmentLength * 0.5)),
                        to: curveMinPos.add(curveMinTangent.mul(referenceSegmentLength * 0.5)),
                        width: referenceSegmentWidth,
                    }
                    const searchSegment = {
                        from: curveMaxPos.add(curveMaxTangent.mul(-referenceSegmentLength * 0.5 - searchDistanceAlongCurve * 0.5)),
                        to: curveMaxPos.add(curveMaxTangent.mul(referenceSegmentLength * 0.5 + searchDistanceAlongCurve * 0.5)),
                        width: referenceSegmentWidth + searchDistanceAcrossCurve,
                    }
                    const lineSearchPenaltyFn = (position: Vector2Like) => {
                        const correctedPosition = Vector2.add(position, curveMaxTangent.mul(referenceSegmentLength * 0.5))
                        const delta = correctedPosition.sub(curveMaxPos)
                        const penaltyAlongCurve = Vector2.dot(delta, curveMaxTangent) * distancePenaltyAlongCurve
                        const penaltyAcrossCurve = Vector2.dot(delta, curveMaxTangent.perp()) * distancePenaltyAcrossCurve
                        // apply penalty in an elliptical shape
                        return Math.sqrt(penaltyAlongCurve * penaltyAlongCurve + penaltyAcrossCurve * penaltyAcrossCurve)
                    }

                    const result = await alignBoundaryImageOpNodeBag.runInFrame(() => {
                        const resultNode = alignBoundaryImageOpNodeBag.getNode("FindBestMatchAlongSegment", ImageOpNodeTilingFindBestMatchAlongSegment, {
                            imageOpHalOptions,
                            sourceImage,
                            referenceSegment,
                            searchSegment,
                            penaltyFn: lineSearchPenaltyFn,
                        })
                        return this.callback.imageOpSystem.evaluate("hal", resultNode)
                    })

                    const correctedCurveMaxPosition = Vector2.add(result.bestMatchPosition, curveMaxTangent.mul(referenceSegmentLength * 0.5)) // + searchDistanceAlongCurve * 0.5))
                    const correctedCurveMaxT = maxCurveInterpolator.solveForT(correctedCurveMaxPosition)
                    // ignore out of range points for now
                    if (t > 0 && t < 1 && correctedCurveMaxT > 0 && correctedCurveMaxT < 1) {
                        minCorrelation = Math.min(minCorrelation, result.peakValue)
                        maxCorrelation = Math.max(maxCorrelation, result.peakValue)
                        alignmentPointInfos.push({
                            boundaryDirection: boundary.direction,
                            t: t,
                            referencePosition: curveMinPos,
                            snapResult: {
                                snappedPosition: correctedCurveMaxPosition,
                                correlation: result.peakValue,
                            },
                        })
                    }
                    currCurveMaxT = correctedCurveMaxT
                    if (i + 1 < numSteps) {
                        currCurveMaxT += stepSize //* (strategy === "backward-integration" ? -1 : 1)
                    }
                }
                const eps = stepSize * 0.1
                const hasSucceeded =
                    strategy === "fixed" || (currCurveMaxT > -eps && currCurveMaxT < eps) || (currCurveMaxT > 1 - eps && currCurveMaxT < 1 + eps)
                if (hasSucceeded) {
                    this.logger.debug("Alignment succeeded with strategy", strategy)
                    return {minCorrelation, maxCorrelation, alignmentPointInfos}
                } else {
                    switch (strategy) {
                        case "forward-integration":
                            // strategy = "backward-integration"
                            strategy = "fixed"
                            break
                        // case "backward-integration":
                        //     strategy = "fixed"
                        //     break
                        case "fixed":
                            throw new Error("Failed to align boundary")
                    }
                }
            }
        } catch (error) {
            this.logger.error("Error aligning boundary", error)
            return undefined
        } finally {
            alignBoundaryImageOpNodeBag.dispose()
            this.callback.setBusy(false)
        }
    }

    private toggleSnapMode() {
        let snapEnabled = this.snapToSimilarFeatureEnabled$.value || this.snapToHelperLinesEnabled$.value
        snapEnabled = !snapEnabled
        this.snapToSimilarFeatureEnabled$.next(snapEnabled)
        this.snapToHelperLinesEnabled$.next(snapEnabled)
    }

    private onTilingAreaChanged(event: ChangeEvent) {
        if (event.type !== "point-result-uv-moved") {
            this.markEdited()
        }
        this.invalidateCachedGrid()
        this.invalidateBorderBlendMask()
        this.requestEval()
    }

    private invalidateCachedGrid() {
        this.cachedGridGeometry = undefined
        this.uploadedGridGeometry = undefined
    }

    override async save(processingJobId: string): Promise<TextureEditNodes.Operator | undefined> {
        this.copyToNode()
        const uploadGridGeometry = async () => {
            this.node.uploadedGridMapping = this.uploadedGridGeometry
                ? {
                      resultSize: this.uploadedGridGeometry.resultSize,
                      meshDataObject: {
                          type: "data-object-reference",
                          dataObjectId: await this.uploadedGridGeometry.dataObjectId,
                      },
                  }
                : undefined
        }
        // const uploadBorderBlendMask = async (borderBlendMask: ManagedDrawableImageHandle | undefined): Promise<MaskReference | undefined> => {
        //     if (!borderBlendMask) {
        //         return undefined
        //     }
        //     return {
        //         type: "data-object-reference",
        //         dataObjectId: await this.callback.drawableImageCache.getDataObjectId(borderBlendMask.ref.id),
        //     }
        // }
        // const uploadHorizontalBorderBlendMask = async () =>
        //     (this.node.borderBlending.horizontalMask = await uploadBorderBlendMask(this.horizontalBorderBlendMask))
        // const uploadVerticalBorderBlendMask = async () => (this.node.borderBlending.verticalMask = await uploadBorderBlendMask(this.verticalBorderBlendMask))
        // await Promise.all([uploadGridGeometry(), uploadHorizontalBorderBlendMask(), uploadVerticalBorderBlendMask()])
        await uploadGridGeometry()
        return super.save(processingJobId)
    }

    private copyToNode() {
        const tilingArea = this.canvasToolbox.tilingArea
        const node = this.node
        const isCornerControlPoint = (index: number, array: Array<unknown>) => index === 0 || index === array.length - 1
        // horizontal sub-division control points
        const boundaryH = tilingArea.spatialMapping.boundaryH
        const horizontalLowBoundCurveControlPoints = boundaryH.curveMin.curveControlPoints
        const horizontalHighBoundCurveControlPoints = boundaryH.curveMax.curveControlPoints
        node.boundaries.horizontal.controlPoints = horizontalLowBoundCurveControlPoints
            .map((curveControlPoint, index) => ({
                type: curveControlPoint.controlPoint.type,
                loBound: {positionPx: curveControlPoint.controlPoint.sourcePosition, normalizedCurvePosition: curveControlPoint.t},
                hiBound: {
                    positionPx: horizontalHighBoundCurveControlPoints[index].controlPoint.sourcePosition,
                    normalizedCurvePosition: horizontalHighBoundCurveControlPoints[index].t,
                },
            }))
            .filter((_, index, array) => !isCornerControlPoint(index, array))
        // vertical sub-division control points
        const boundaryV = tilingArea.spatialMapping.boundaryV
        const verticalLowBoundCurveControlPoints = boundaryV.curveMin.curveControlPoints
        const verticalHighBoundCurveControlPoints = boundaryV.curveMax.curveControlPoints
        node.boundaries.vertical.controlPoints = verticalLowBoundCurveControlPoints
            .map((curveControlPoint, index) => ({
                type: curveControlPoint.controlPoint.type,
                loBound: {positionPx: curveControlPoint.controlPoint.sourcePosition, normalizedCurvePosition: curveControlPoint.t},
                hiBound: {
                    positionPx: verticalHighBoundCurveControlPoints[index].controlPoint.sourcePosition,
                    normalizedCurvePosition: verticalHighBoundCurveControlPoints[index].t,
                },
            }))
            .filter((_, index, array) => !isCornerControlPoint(index, array))
        // corner control points
        node.cornerControlPoints.topLeft.positionPx = horizontalLowBoundCurveControlPoints[0].controlPoint.sourcePosition
        node.cornerControlPoints.topRight.positionPx =
            horizontalLowBoundCurveControlPoints[horizontalLowBoundCurveControlPoints.length - 1].controlPoint.sourcePosition
        node.cornerControlPoints.bottomLeft.positionPx = horizontalHighBoundCurveControlPoints[0].controlPoint.sourcePosition
        node.cornerControlPoints.bottomRight.positionPx =
            horizontalHighBoundCurveControlPoints[horizontalHighBoundCurveControlPoints.length - 1].controlPoint.sourcePosition

        // helper lines
        if (!node.helperLines) {
            node.helperLines = {
                curves: [],
                boundaryFollows: false,
            }
        }
        node.helperLines.curves = this.canvasToolbox.helperLinesBag.helperLines.map((helperLine) => ({
            origin: helperLine.sourceCastRayOrigin,
            target: helperLine.sourceCastRayTarget,
            points: helperLine.points,
        }))
    }

    async computeSnapPosition(parameters: {position: Vector2; referencePosition: Vector2 | undefined} | undefined): Promise<Vector2Like | undefined> {
        if (!parameters) {
            this.canvasToolbox.showSnapRadius(undefined)
            return undefined
        }
        const snapDistance = Math.round(this.snapDistancePx$.value)
        if (snapDistance <= 0) {
            return undefined
        }
        const referencePosition = parameters.referencePosition
        const snapToHelperLinesEnabled = this.snapToHelperLinesEnabled$.value && this.hasHelperLines
        const snapToSimilarFeatureEnabled = this.snapToSimilarFeatureEnabled$.value && referencePosition
        if (snapToHelperLinesEnabled || snapToSimilarFeatureEnabled) {
            // only show snap area if there is anything to snap to
            this.canvasToolbox.showSnapRadius({
                position: parameters.position,
                snapDistancePx: snapDistance,
            })
        }
        let snapPosition: Vector2Like | undefined
        if (snapToHelperLinesEnabled) {
            snapPosition = this.snapToHelpers(parameters.position, snapDistance)
        }
        if (!snapPosition && snapToSimilarFeatureEnabled) {
            // if a new snap request is made while the previous computation is still running, we return the previous result
            snapPosition = await this._snapToFeatureReentrancyGuard
                .startIfIdleOrGetCurrent(async () => {
                    return await this.snapToSimilarFeature(parameters.position, referencePosition, snapDistance).then(
                        (snapResult) => snapResult?.snappedPosition,
                    )
                })
                .catch((e) => {
                    // throw as an unhandled exception
                    setTimeout(() => {
                        throw e
                    }, 0)
                    return undefined
                })
        }
        return snapPosition
    }

    private snapToHelpers(position: Vector2Like, snapDistance: number): Vector2 | undefined {
        const crossingDistanceDistanceScale = 0.5 // scale factor to make crossing distances more important than line distances
        const crossingSnapDistance = Math.min(snapDistance * 0.2, 20 / this.canvasToolbox.callback.zoomLevel) // distance under which to snap to crossings
        // search crossings
        const resultCrossings = this.canvasToolbox.helperLinesBag.helperLineCrossings.reduce(
            (result, helperLineCrossing) => {
                const distance = Vector2.distance(helperLineCrossing.point, position)
                if (!result || distance < result.distance) {
                    return {closestPoint: helperLineCrossing.point, distance}
                } else {
                    return result
                }
            },
            undefined as {closestPoint: Vector2; distance: number} | undefined,
        )
        if (resultCrossings && resultCrossings.distance < crossingSnapDistance) {
            return resultCrossings.closestPoint
        }
        // search lines
        const resultLines = this.canvasToolbox.helperLinesBag.getClosestPoint(position)
        if (!resultCrossings && !resultLines) {
            return undefined
        }
        let minDistance: number
        let minClosestPoint: Vector2
        if (resultCrossings && resultLines) {
            const crossingsAreCloser = resultCrossings.distance * crossingDistanceDistanceScale < resultLines.distance
            minDistance = crossingsAreCloser ? resultCrossings.distance : resultLines.distance
            minClosestPoint = crossingsAreCloser ? resultCrossings.closestPoint : resultLines.closestPoint
        } else if (resultCrossings) {
            minDistance = resultCrossings.distance
            minClosestPoint = resultCrossings.closestPoint
        } else if (resultLines) {
            minDistance = resultLines.distance
            minClosestPoint = resultLines.closestPoint
        } else {
            throw new Error("This should not happen")
        }
        if (minDistance > snapDistance) {
            return undefined
        }
        return minClosestPoint
    }

    private async snapToSimilarFeature(position: Vector2Like, referencePosition: Vector2Like, snapDistance: number): Promise<CorrelationResult | undefined> {
        const start = this.debugDrawEnabled$.value ? performance.now() : 0

        const imageOpHalOptions: ImageOpHalOptions = {
            usePhysicalOutputImage: true,
        }

        const maxSearchRadius = 1024
        const snapDistancePenalty = 1 - snapDistance / maxSearchRadius
        this.canvasToolbox.clearDebugRects()
        const debugImage = undefined // this.debugDrawEnabled$.value ? this.debugImage : undefined

        const sourceImage = this.callback.selectedOperatorInput
        if (!sourceImage) {
            throw new Error("No source image")
        }

        // const numLevels = Math.ceil(Math.log2(maxSearchRadius))
        // const maxTemplateSize = 2 ** (numLevels - 1) * correlationWindowSize
        // const maxSourceImageSize = 2 ** (numLevels - 1) * (correlationWindowSize + searchSize - 1)

        // compute regions
        // const templateRegion = {
        //     x: Math.round(referencePosition.x - maxTemplateSize / 2),
        //     y: Math.round(referencePosition.y - maxTemplateSize / 2),
        //     width: maxTemplateSize,
        //     height: maxTemplateSize,
        // }
        // if (this.debugDrawEnabled$.value) {
        //     this.canvasToolbox.createDebugRect(templateRegion, "green")
        // }
        // const sourceRegion = {
        //     x: Math.round(position.x - maxSourceImageSize / 2),
        //     y: Math.round(position.y - maxSourceImageSize / 2),
        //     width: maxSourceImageSize,
        //     height: maxSourceImageSize,
        // }
        // if (this.debugDrawEnabled$.value) {
        //     this.canvasToolbox.createDebugRect(sourceRegion, "blue")
        // }
        const convertSnapDistancePenalty = (snapDistancePenalty: number) => (4 * snapDistancePenalty) / maxSearchRadius
        const correlationPenaltyPerPixel = convertSnapDistancePenalty(snapDistancePenalty)

        const sourceOffsetAndCorrelationData = await this.snapToSimilarFeatureImageOpNodeBag.runInFrame(() => {
            // debugImage?.init(cmdQueue, {width: 512, height: 4096}, {r: 0.05, g: 0.05, b: 0.05, a: 1})
            const sourceOffsetAndCorrelation = this.snapToSimilarFeatureImageOpNodeBag.getNode(
                "HierarchicalCrossCorrelation",
                ImageOpNodeTilingHierarchicalCrossCorrelation,
                {
                    imageOpHalOptions,
                    sourceImage: sourceImage,
                    sourceImageReferencePosition: position,
                    templateImage: sourceImage,
                    templateImageReferencePosition: referencePosition,
                    maxSearchRadius: maxSearchRadius,
                    correlationPenaltyPerPixel: correlationPenaltyPerPixel,
                    // cacheData: this.hierarchicalCrossCorrelationCacheData,
                    // debugImage: this.debugDrawEnabled$.value ? debugImage : undefined,
                    // debugRectFn: this.debugDrawEnabled$.value ? (rect, color) => this.canvasToolbox.createDebugRect(rect, color) : undefined,
                },
            )
            return this.callback.imageOpSystem.evaluate("hal", sourceOffsetAndCorrelation)
        })

        // const sourceOffsetAndCorrelationHalImage = await sourceOffsetAndCorrelation.getUpdatedHalImage({x: 0, y: 0, width: 1, height: 1, mipLevel: 0})
        // if (!sourceOffsetAndCorrelationHalImage) {
        //     throw new Error("Failed to get source offset and correlation image")
        // }
        // const sourceOffsetAndCorrelationData = sourceOffsetAndCorrelationHalImage.halImage.readImageDataFloat()
        // sourceOffsetAndCorrelationHalImage.release()
        // const sourceOffsetVec = new Vector2(sourceOffsetAndCorrelationData[0], sourceOffsetAndCorrelationData[1])
        // const correlation = sourceOffsetAndCorrelationData[2]
        const snappedPosition = sourceOffsetAndCorrelationData.sourceOffset //sourceOffsetVec //.add(position)

        if (debugImage) {
            this.requestEval()
        }

        if (this.debugDrawEnabled$.value) {
            const end = performance.now()
            this.logger.debug("Snap time:", end - start, "ms")
            this.logger.debug("Snapped position:", snappedPosition)
            this.logger.debug("Correlation:", sourceOffsetAndCorrelationData.correlation)
        }

        return sourceOffsetAndCorrelationData.correlation >= -1 ? {snappedPosition, correlation: sourceOffsetAndCorrelationData.correlation} : undefined
    }

    get maxBorderBlendingPx() {
        return this._maxBorderBlendingPx
    }

    readonly minBorderBlendingPx = 4
    readonly numGridControlPointSegmentSubdivisions = 4 // rather than constant subdivision this should be more adaptive to the actual non-linearity imposed by the grid

    private _pendingValidationData: {tessellatedGridPoints: GridPoint[][]; borderBlendDistance: number} | null = null
    private readonly _captureSourceSize = (args: {sourceImage: AnyImageOpResult}): AnyImageOpResult => {
        if (this._pendingValidationData) {
            const {tessellatedGridPoints, borderBlendDistance} = this._pendingValidationData
            this._pendingValidationData = null
            const {width, height} = args.sourceImage.descriptor
            const isWithinBounds = tessellatedGridPoints.every((row) =>
                row.every((pt) => pt.sourcePixel.x >= 0 && pt.sourcePixel.x <= width && pt.sourcePixel.y >= 0 && pt.sourcePixel.y <= height),
            )
            if (isWithinBounds) {
                this.setValid()
            } else {
                this.setInvalid(
                    borderBlendDistance > 0
                        ? "The tiling region including the border blending area exceeds the image bounds."
                        : "The tiling region exceeds the image bounds.",
                )
            }
        }
        return args.sourceImage
    }

    private _maxBorderBlendingPx = 512
    private uploadService: UploadGqlService
    private destroyed = new Subject<void>()
    private cachedGridGeometry?: GridMappingResultType
    private uploadedGridGeometry?: UploadedGridGeometry
    private readonly $alignmentInfo = signal<AlignmentInfo | undefined>(undefined)
    private readonly $currentHelperLineTracer = signal<HelperLineTracer | undefined>(undefined)
    private readonly $hasAlignmentControlPoints = signal(false)
    private readonly $hasSelectedHelperLines = signal(false)
    private readonly $traceHelperLineEditModeEnabled = signal(false)
    private readonly $isComputingAlignment = signal(false)
    private sharedImageOpNodeBag = new ImageOpNodeBag("OperatorTilingShared")
    private snapToSimilarFeatureImageOpNodeBag = new ImageOpNodeBag("SnapToSimilarFeatureImageOpNodeBag")
    private _snapToFeatureReentrancyGuard = new AsyncReentrancyGuard.PromiseGate<Vector2Like | undefined>()
    private horizontalBorderMaskCache = new BakedMaskCache(this.callback.texturesApi, this.callback.organization.legacyId)
    private verticalBorderMaskCache = new BakedMaskCache(this.callback.texturesApi, this.callback.organization.legacyId)
    private isInitializing = true
}

type UploadedGridGeometry = {
    dataObjectId: Promise<string>
    resultSize: Size2Like
}

type AlignmentPointInfo = {
    boundaryDirection: BoundaryDirection
    t: number
    referencePosition: Vector2
    // originalPosition: Vector2
    snapResult: {
        snappedPosition: Vector2
        correlation: number
    }
    // boundarySlope: Vector2
    curveControlPoints?: {
        curveControlPoint0: BoundaryCurveControlPoint
        curveControlPoint1: BoundaryCurveControlPoint
    }
}

type AlignmentInfo = {
    textureType: TextureType // texture type used to create the alignment info
    points: AlignmentPointInfo[]
    alignmentQuality: number
}

type CorrelationResult = {
    snappedPosition: Vector2Like
    correlation: number
}

const initialControlPointInsetRatio = 0.1
